import { useState, useEffect } from "react";
import Card from "components/card";
import { MdCreate, MdDelete, MdUpload } from "react-icons/md";
import { toast, ToastContainer } from "react-toastify";
import Swal from "sweetalert2";
import {
  getTestimonialDataApi,
  addTestimonialApi,
  updateTestimonialApi,
  deleteTestimonialApi,
} from "Services/allApi";
import { IoEye } from "react-icons/io5";

const TestimonialCards = ({ extra, defimage }) => {
  const [testimonialData, setTestimonialData] = useState([]);
  const [formData, setFormData] = useState({
    student_image: null,
    content: "",
    student_name: "",
    course: "",
    role: "",
    company: "",
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [currentTestimonialId, setCurrentTestimonialId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Fetch testimonials on component mount
  useEffect(() => {
    fetchTestimonials(currentPage);
  }, [currentPage]);

  const fetchTestimonials = async (page) => {
    try {
      const response = await getTestimonialDataApi(page);
      console.log("Full API Response:", response);

      if (response?.status === 200 && response.data?.results) {
        console.log("Response Data:", response.data.results);
        setTestimonialData(response.data.results);
        setTotalPages(Math.ceil(response.data.count / 10));
      } else {
        console.log("API response does not contain expected 'results' array.");
        setTestimonialData([]);
      }
    } catch (error) {
      console.error("Error fetching Testimonials:", error);
      setTestimonialData([]);
    }
  };

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setFormData((prev) => ({ ...prev, student_image: file }));
    }
  };

  const handleEdit = (testimonial) => {
    setFormData({
      student_image: null,
      content: testimonial.content || "",
      student_name: testimonial.student_name || "",
      course: testimonial.course || "",
      role: testimonial.role || "",
      company: testimonial.company || "",
    });
    setCurrentTestimonialId(testimonial.id);
    setIsModalOpen(true);
  };

  const handleAdd = () => {
    setFormData({
      student_image: null,
      content: "",
      student_name: "",
      course: "",
      role: "",
      company: "",
    });
    setCurrentTestimonialId(null);
    setIsAddModalOpen(true);
  };

  const handleSave = async () => {
    const data = new FormData();
    if (formData.student_image) {
      data.append("student_image", formData.student_image);
    }
    data.append("course", formData.course);
    data.append("student_name", formData.student_name);
    data.append("content", formData.content);
    data.append("role", formData.role);
    data.append("company", formData.company);

    try {
      if (isAddModalOpen) {
        const response = await addTestimonialApi(data);
        if (!response.error) {
          setTestimonialData((prev) => [...prev, response.data]);
          toast.success("Testimonial added successfully.");
        } else {
          toast.error(response.message);
        }
      } else if (isModalOpen && currentTestimonialId) {
        const response = await updateTestimonialApi(currentTestimonialId, data);
        if (!response.error) {
          setTestimonialData((prev) =>
            prev.map((t) =>
              t.id === currentTestimonialId ? response.data : t
            )
          );
          toast.success("Testimonial updated successfully.");
        } else {
          toast.error(response.message);
        }
      }
    } catch (error) {
      toast.error("Error saving testimonial data.");
    }

    setIsModalOpen(false);
    setIsAddModalOpen(false);
  };

  const handleDelete = async (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "Do you want to delete this testimonial?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
      customClass: {
        popup: 'dark:bg-gray-800 dark:text-white', // Dark background and white text
        title: 'dark:text-white',
        confirmButton: 'dark:bg-blue-500 dark:hover:bg-blue-700 dark:text-white',
        cancelButton: 'dark:bg-red-500 dark:hover:bg-red-700 dark:text-white'
    }
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await deleteTestimonialApi(id);
          if (!response.error) {
            setTestimonialData((prev) => prev.filter((t) => t.id !== id));
            toast.success("Testimonial deleted successfully.");
          } else {
            toast.error(response.message);
          }
        } catch (error) {
          toast.error("Error deleting testimonial.");
        }
      }
    });
  };

  const handlePageChange = (direction) => {
    if (direction === "next" && currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    } else if (direction === "previous" && currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };


  const handleModalClose = () => {
    setIsModalOpen(false);
    setIsAddModalOpen(false);
  };

  return (
    <>
      <div className="mb-4 flex justify-end mt-4">
        <button
          onClick={handleAdd}
          className="bg-purple-800 text-white px-4 py-2 rounded-lg font-medium text-sm hover:bg-purple-700"
        >
          Add Testimonial
        </button>
      </div>
      <div>
        {testimonialData.map((testimonial) => (
          <Card
            key={testimonial.id}
            extra={"w-full p-4 h-full mb-3"}
          >
            <div className="relative flex flex-row gap-8 mb-4">
              <img
                width={'100px'}
                height={'100px'}
                src={testimonial.student_image || defimage}
                alt="Testimonial"
                className="rounded-xl"
              />
              <div className="flex flex-col justify-center">
                <span className="text-lg font-bold text-black-700 dark:text-white">
                  {testimonial.student_name || "student_name"}
                </span>
                <span className="text-sm text-black-600 dark:text-gray-300">
                  {testimonial.company || "Company"}
                </span>
                <span className="text-sm text-black-600 dark:text-gray-300">
                  {testimonial.role || "Role"}
                </span>
              </div>
            </div>
            <div className="h-full">

              <div className="mb-4">
                <p className="text-sm text-black-600 dark:text-gray-300">
                  {testimonial.content || "Content"}
                </p>
              </div>
              <button
                onClick={() => handleEdit(testimonial)}
                className="rounded-lg px-4 py-2 text-green-700 font-medium text-2xl transition duration-200 hover:bg-purple-700 float-end"
              >
                <MdCreate />
              </button>

              <button
                onClick={() => handleDelete(testimonial.id)}
                className="rounded-lg px-4 py-2 text-red-600 font-medium text-2xl transition duration-200 hover:bg-purple-600 float-end"
              >
                <MdDelete />
              </button>
            </div>
          </Card>
        ))}
      </div>
      <div className="flex justify-center items-center mt-4 space-x-4">
        <button
          className="rounded-lg bg-gray-300 px-4 py-2 text-sm font-medium hover:bg-gray-400"
          disabled={currentPage === 1}
          onClick={() => handlePageChange("previous")}
        >
          Previous
        </button>
        <span className="text-gray-700">Page {currentPage} of {totalPages}</span>
        <button
          className="rounded-lg bg-gray-300 px-4 py-2 text-sm font-medium hover:bg-gray-400"
          disabled={currentPage === totalPages}
          onClick={() => handlePageChange("next")}
        >
          Next
        </button>
      </div>

      {(isModalOpen || isAddModalOpen) && (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded-xl w-1/2 max-h-screen overflow-y-auto  dark:!bg-navy-700 dark:text-white">
            <h2 className="text-lg font-semibold text-gray-700 dark:text-white">
              {isAddModalOpen ? "Add Testimonial" : "Edit Testimonial"}
            </h2>
            <div className="mt-4">
              <div className="flex gap-4 mb-4">
                <div className="w-1/2">
                  <label className="block text-sm text-gray-700">Course</label>
                  <select
                    value={formData.course}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        course: e.target.value,
                      }))
                    }
                    className="w-full mt-1 p-2 border rounded-md bg-white  dark:!bg-navy-700 dark:text-white"
                  >
                    <option value="">Select a Course</option>
                    <option value="1">JAVA SPRING FULL STACK</option>
                    <option value="2">ROBOTICS With AI & IoT</option>
                    <option value="3">MEA(R)N Stack Web Development Expert - Angular & React</option>
                    <option value="4">
                    Software Testing Training - Manual & Automation</option>
                    <option value="5">PYTHON DATA SCIENCE - ML - AI - & Power BI</option>
                    <option value="6">Python Django - React - Full Stack</option>
                    <option value="7">Asp.net MVC with Angular - Full Stack</option>
                    <option value="8">Flutter Developer</option>
                  </select>
                </div>

                <div className="w-1/2">
                  <label className="block text-sm text-gray-700">Name</label>
                  <input
                    type="text"
                    value={formData.student_name}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        student_name: e.target.value,
                      }))
                    }
                    className="w-full mt-1 p-2 border rounded-md  dark:!bg-navy-700 dark:text-white"
                  />
                </div>
              </div>
              <div className="flex gap-4 mb-4">
                <div className="w-1/2">
                  <label className="block text-sm text-gray-700">Role</label>
                  <input
                    type="text"
                    value={formData.role}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, role: e.target.value }))
                    }
                    className="w-full mt-1 p-2 border rounded-md  dark:!bg-navy-700 dark:text-white"
                  />
                </div>
                <div className="w-1/2">
                  <label className="block text-sm text-gray-700">Company</label>
                  <input
                    type="text"
                    value={formData.company}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        company: e.target.value,
                      }))
                    }
                    className="w-full mt-1 p-2 border rounded-md  dark:!bg-navy-700 dark:text-white"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm text-gray-700">content</label>
                <textarea
                  value={formData.content}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, content: e.target.value }))
                  }
                  className="w-full mt-1 p-2 border border-gray-300 rounded-md  dark:!bg-navy-700 dark:text-white"
                />
              </div>
              <div className="mb-4 flex items-center">
                <div>
                  {formData.student_image && (
                    <img
                      src={URL.createObjectURL(formData.student_image)}
                      alt="Preview"
                      className="w-20 h-20 object-cover"
                    />
                  )}
                  {!formData.student_image && currentTestimonialId && (
                    <img
                      src={
                        testimonialData.find((t) => t.id === currentTestimonialId)
                          ?.student_image || defimage
                      }
                      alt="Current"
                      className="w-20 h-20 object-cover rounded"
                    />
                  )}
                </div>
                <div className="ml-4">
                  <label className="block text-sm text-gray-700">Image</label>
                  <input
                    type="file"
                    id="file-upload"
                    onChange={handleImageChange}
                    accept="image/*"
                    className="hidden"
                  />
                  <button
                    onClick={() => document.getElementById("file-upload").click()}
                    className="flex items-center justify-center w-10 p-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
                  >
                    <MdUpload className="w-5 h-5" />
                  </button>
                  <p className="mt-2 text-xs">
                    The height and width must be 1080 × 1350
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-4 mt-6">
                <button
                  onClick={handleModalClose}
                  className="bg-gray-500 text-white px-4 py-2 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg"
                >
                  {isAddModalOpen ? "Save" : "Update"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


      <ToastContainer
        position="top-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeButton={true}
      />
    </>
  );
};

export default TestimonialCards;